import React, { useState, useEffect, Suspense } from 'react';
import IASideBar from './IASideBar';
import * as mui from '@mui/material';
import ResponsiveContainer from '../../layout/Container';
import { useParams, useNavigate } from 'react-router-dom';
import auditService from '../../../services/AuditService';
import { enqueueSnackbar } from 'notistack';
import * as Icons from '@mui/icons-material';

const PlanningDocuments = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [planDocuments, setPlanDocuments] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newDocumentType, setNewDocumentType] = useState('');
  const [customDocumentName, setCustomDocumentName] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);

  // Define document types with icons and descriptions
  const documentTypes = [
    { 
      id: 'General', 
      name: 'General Audit Plan', 
      icon: <Icons.Assignment />,
      description: 'Standard audit planning document covering general audit activities.',
      color: '#2563eb' 
    },
    { 
      id: 'IT Audit', 
      name: 'IT Audit Plan', 
      icon: <Icons.Computer />,
      description: 'Specialized plan for information technology auditing, covering system controls, application reviews, and IT governance.',
      color: '#16a34a' 
    },
    { 
      id: 'Financial Audit', 
      name: 'Financial Audit Plan', 
      icon: <Icons.AccountBalance />,
      description: 'Detailed planning for financial statement audits, including account testing and compliance procedures.',
      color: '#9333ea' 
    },
    { 
      id: 'Operational Audit', 
      name: 'Operational Audit Plan', 
      icon: <Icons.Settings />,
      description: 'Plan for evaluating operational efficiency and effectiveness of business processes.',
      color: '#f59e0b' 
    },
    { 
      id: 'SOX', 
      name: 'SOX Compliance Plan', 
      icon: <Icons.Gavel />,
      description: 'Sarbanes-Oxley compliance audit planning document with control testing activities.',
      color: '#dc2626' 
    },
    { 
      id: 'SOC', 
      name: 'SOC Audit Plan', 
      icon: <Icons.Security />,
      description: 'Service Organization Control audit plan with security, availability, and process integrity tests.',
      color: '#0891b2' 
    },
    { 
      id: 'Custom', 
      name: 'Custom Plan', 
      icon: <Icons.Edit />,
      description: 'Create a custom planning document with specialized structure for your specific audit needs.',
      color: '#6b7280' 
    }
  ];

  // Define template library with predefined activities for each document type
  const templateLibrary = {
    // General Audit Plan template
    'General': [
      {
        phase: 'Planning',
        order: 1,
        activities: [
          { activity: 'Define audit scope and objectives', status: 'Not Started', sequence: 0 },
          { activity: 'Conduct preliminary risk assessment', status: 'Not Started', sequence: 1 },
          { activity: 'Schedule kickoff meeting with stakeholders', status: 'Not Started', sequence: 2 },
          { activity: 'Develop audit timeline and resource allocation', status: 'Not Started', sequence: 3 },
          { activity: 'Identify key audit areas based on risk assessment', status: 'Not Started', sequence: 4 }
        ]
      },
      {
        phase: 'Fieldwork',
        order: 2,
        activities: [
          { activity: 'Conduct interviews with key personnel', status: 'Not Started', sequence: 0 },
          { activity: 'Gather and analyze documentation', status: 'Not Started', sequence: 1 },
          { activity: 'Perform compliance testing of controls', status: 'Not Started', sequence: 2 },
          { activity: 'Document testing results and findings', status: 'Not Started', sequence: 3 }
        ]
      },
      {
        phase: 'Reporting',
        order: 3,
        activities: [
          { activity: 'Draft preliminary findings and recommendations', status: 'Not Started', sequence: 0 },
          { activity: 'Conduct closing meeting with management', status: 'Not Started', sequence: 1 },
          { activity: 'Finalize audit report', status: 'Not Started', sequence: 2 },
          { activity: 'Submit report for approval', status: 'Not Started', sequence: 3 }
        ]
      },
      {
        phase: 'Follow-up',
        order: 4,
        activities: [
          { activity: 'Track implementation of recommendations', status: 'Not Started', sequence: 0 },
          { activity: 'Conduct follow-up reviews as needed', status: 'Not Started', sequence: 1 },
          { activity: 'Prepare status report on remediation efforts', status: 'Not Started', sequence: 2 }
        ]
      }
    ],
    
    // IT Audit Plan template
    'IT Audit': [
      {
        phase: 'Planning',
        order: 1,
        activities: [
          { activity: 'Define IT audit scope and objectives', status: 'Not Started', sequence: 0 },
          { activity: 'Inventory critical IT systems and applications', status: 'Not Started', sequence: 1 },
          { activity: 'Perform IT risk assessment', status: 'Not Started', sequence: 2 },
          { activity: 'Schedule kickoff meeting with IT stakeholders', status: 'Not Started', sequence: 3 },
          { activity: 'Request system documentation and access', status: 'Not Started', sequence: 4 }
        ]
      },
      {
        phase: 'Access Controls',
        order: 2,
        activities: [
          { activity: 'Review user access management processes', status: 'Not Started', sequence: 0 },
          { activity: 'Test privileged access controls', status: 'Not Started', sequence: 1 },
          { activity: 'Evaluate segregation of duties', status: 'Not Started', sequence: 2 },
          { activity: 'Assess password policies and enforcement', status: 'Not Started', sequence: 3 },
          { activity: 'Review authentication mechanisms', status: 'Not Started', sequence: 4 }
        ]
      },
      {
        phase: 'System Security',
        order: 3,
        activities: [
          { activity: 'Evaluate network security controls', status: 'Not Started', sequence: 0 },
          { activity: 'Review patch management procedures', status: 'Not Started', sequence: 1 },
          { activity: 'Assess malware protection measures', status: 'Not Started', sequence: 2 },
          { activity: 'Test incident response procedures', status: 'Not Started', sequence: 3 },
          { activity: 'Review security logging and monitoring', status: 'Not Started', sequence: 4 }
        ]
      },
      {
        phase: 'Data Protection',
        order: 4,
        activities: [
          { activity: 'Review data classification procedures', status: 'Not Started', sequence: 0 },
          { activity: 'Assess data encryption implementation', status: 'Not Started', sequence: 1 },
          { activity: 'Evaluate backup and recovery processes', status: 'Not Started', sequence: 2 },
          { activity: 'Review data retention policies', status: 'Not Started', sequence: 3 }
        ]
      },
      {
        phase: 'Reporting',
        order: 5,
        activities: [
          { activity: 'Document IT audit findings', status: 'Not Started', sequence: 0 },
          { activity: 'Develop remediation recommendations', status: 'Not Started', sequence: 1 },
          { activity: 'Present findings to IT management', status: 'Not Started', sequence: 2 },
          { activity: 'Finalize IT audit report', status: 'Not Started', sequence: 3 }
        ]
      }
    ],
    
    // Financial Audit Plan template
    'Financial Audit': [
      {
        phase: 'Planning',
        order: 1,
        activities: [
          { activity: 'Define financial audit scope and materiality', status: 'Not Started', sequence: 0 },
          { activity: 'Review prior audit reports and findings', status: 'Not Started', sequence: 1 },
          { activity: 'Perform analytical procedures on financial statements', status: 'Not Started', sequence: 2 },
          { activity: 'Assess fraud risk factors', status: 'Not Started', sequence: 3 },
          { activity: 'Develop sampling methodology', status: 'Not Started', sequence: 4 }
        ]
      },
      {
        phase: 'Internal Controls',
        order: 2,
        activities: [
          { activity: 'Document understanding of control environment', status: 'Not Started', sequence: 0 },
          { activity: 'Test controls over financial reporting', status: 'Not Started', sequence: 1 },
          { activity: 'Evaluate segregation of duties in finance', status: 'Not Started', sequence: 2 },
          { activity: 'Assess IT controls relevant to financial reporting', status: 'Not Started', sequence: 3 }
        ]
      },
      {
        phase: 'Substantive Testing',
        order: 3,
        activities: [
          { activity: 'Perform cash and cash equivalents testing', status: 'Not Started', sequence: 0 },
          { activity: 'Test accounts receivable and revenue recognition', status: 'Not Started', sequence: 1 },
          { activity: 'Verify inventory valuation and existence', status: 'Not Started', sequence: 2 },
          { activity: 'Test fixed assets and depreciation', status: 'Not Started', sequence: 3 },
          { activity: 'Validate accounts payable and expenses', status: 'Not Started', sequence: 4 },
          { activity: 'Test payroll expenses and liabilities', status: 'Not Started', sequence: 5 }
        ]
      },
      {
        phase: 'Financial Reporting',
        order: 4,
        activities: [
          { activity: 'Review financial statement preparation process', status: 'Not Started', sequence: 0 },
          { activity: 'Test financial statement disclosures', status: 'Not Started', sequence: 1 },
          { activity: 'Evaluate accounting policies and estimates', status: 'Not Started', sequence: 2 },
          { activity: 'Perform subsequent events review', status: 'Not Started', sequence: 3 }
        ]
      },
      {
        phase: 'Reporting',
        order: 5,
        activities: [
          { activity: 'Summarize audit adjustments and findings', status: 'Not Started', sequence: 0 },
          { activity: 'Draft financial audit report', status: 'Not Started', sequence: 1 },
          { activity: 'Present findings to management', status: 'Not Started', sequence: 2 },
          { activity: 'Issue final audit opinion', status: 'Not Started', sequence: 3 }
        ]
      }
    ],
    
    // Operational Audit Plan template
    'Operational Audit': [
      {
        phase: 'Planning',
        order: 1,
        activities: [
          { activity: 'Define operational areas for review', status: 'Not Started', sequence: 0 },
          { activity: 'Identify key performance indicators', status: 'Not Started', sequence: 1 },
          { activity: 'Develop process flow documentation', status: 'Not Started', sequence: 2 },
          { activity: 'Conduct preliminary interviews with process owners', status: 'Not Started', sequence: 3 },
          { activity: 'Schedule site visits and observations', status: 'Not Started', sequence: 4 }
        ]
      },
      {
        phase: 'Process Analysis',
        order: 2,
        activities: [
          { activity: 'Map current state processes', status: 'Not Started', sequence: 0 },
          { activity: 'Identify process bottlenecks and constraints', status: 'Not Started', sequence: 1 },
          { activity: 'Evaluate workflow efficiency', status: 'Not Started', sequence: 2 },
          { activity: 'Analyze resource utilization', status: 'Not Started', sequence: 3 },
          { activity: 'Benchmark against industry standards', status: 'Not Started', sequence: 4 }
        ]
      },
      {
        phase: 'Control Assessment',
        order: 3,
        activities: [
          { activity: 'Evaluate operational controls', status: 'Not Started', sequence: 0 },
          { activity: 'Assess compliance with policies and procedures', status: 'Not Started', sequence: 1 },
          { activity: 'Test operational metrics and reporting', status: 'Not Started', sequence: 2 },
          { activity: 'Review quality assurance processes', status: 'Not Started', sequence: 3 }
        ]
      },
      {
        phase: 'Efficiency Review',
        order: 4,
        activities: [
          { activity: 'Identify cost-saving opportunities', status: 'Not Started', sequence: 0 },
          { activity: 'Evaluate automation potential', status: 'Not Started', sequence: 1 },
          { activity: 'Assess workforce productivity', status: 'Not Started', sequence: 2 },
          { activity: 'Analyze waste reduction possibilities', status: 'Not Started', sequence: 3 },
          { activity: 'Review vendor management processes', status: 'Not Started', sequence: 4 }
        ]
      },
      {
        phase: 'Reporting',
        order: 5,
        activities: [
          { activity: 'Document operational findings and recommendations', status: 'Not Started', sequence: 0 },
          { activity: 'Quantify potential efficiency gains', status: 'Not Started', sequence: 1 },
          { activity: 'Present improvement opportunities to management', status: 'Not Started', sequence: 2 },
          { activity: 'Develop implementation roadmap', status: 'Not Started', sequence: 3 }
        ]
      }
    ],
    
    // SOX Compliance Plan template
    'SOX': [
      {
        phase: 'Scoping',
        order: 1,
        activities: [
          { activity: 'Identify significant accounts and disclosures', status: 'Not Started', sequence: 0 },
          { activity: 'Determine financially relevant systems', status: 'Not Started', sequence: 1 },
          { activity: 'Identify significant locations and business units', status: 'Not Started', sequence: 2 },
          { activity: 'Document risk assessment for scoping decisions', status: 'Not Started', sequence: 3 },
          { activity: 'Confirm scope with management and external auditors', status: 'Not Started', sequence: 4 }
        ]
      },
      {
        phase: 'Entity-Level Controls',
        order: 2,
        activities: [
          { activity: 'Evaluate control environment', status: 'Not Started', sequence: 0 },
          { activity: 'Assess risk assessment process', status: 'Not Started', sequence: 1 },
          { activity: 'Review information and communication systems', status: 'Not Started', sequence: 2 },
          { activity: 'Test monitoring controls', status: 'Not Started', sequence: 3 },
          { activity: 'Evaluate anti-fraud controls', status: 'Not Started', sequence: 4 }
        ]
      },
      {
        phase: 'Process Documentation',
        order: 3,
        activities: [
          { activity: 'Update process narratives and flowcharts', status: 'Not Started', sequence: 0 },
          { activity: 'Identify key process controls', status: 'Not Started', sequence: 1 },
          { activity: 'Map controls to financial assertions', status: 'Not Started', sequence: 2 },
          { activity: 'Document IT general controls', status: 'Not Started', sequence: 3 },
          { activity: 'Validate process documentation with control owners', status: 'Not Started', sequence: 4 }
        ]
      },
      {
        phase: 'Testing',
        order: 4,
        activities: [
          { activity: 'Develop test plans for key controls', status: 'Not Started', sequence: 0 },
          { activity: 'Perform design effectiveness testing', status: 'Not Started', sequence: 1 },
          { activity: 'Conduct operating effectiveness testing', status: 'Not Started', sequence: 2 },
          { activity: 'Test IT general controls', status: 'Not Started', sequence: 3 },
          { activity: 'Evaluate segregation of duties', status: 'Not Started', sequence: 4 },
          { activity: 'Document test results and exceptions', status: 'Not Started', sequence: 5 }
        ]
      },
      {
        phase: 'Remediation',
        order: 5,
        activities: [
          { activity: 'Develop remediation plans for control deficiencies', status: 'Not Started', sequence: 0 },
          { activity: 'Implement remediation actions', status: 'Not Started', sequence: 1 },
          { activity: 'Test remediated controls', status: 'Not Started', sequence: 2 },
          { activity: 'Update risk assessment for unremediated issues', status: 'Not Started', sequence: 3 }
        ]
      },
      {
        phase: 'Reporting',
        order: 6,
        activities: [
          { activity: 'Evaluate control deficiencies for severity', status: 'Not Started', sequence: 0 },
          { activity: 'Draft management assessment of ICFR', status: 'Not Started', sequence: 1 },
          { activity: 'Present results to Audit Committee', status: 'Not Started', sequence: 2 },
          { activity: 'Coordinate with external auditors', status: 'Not Started', sequence: 3 },
          { activity: 'Finalize SOX certification', status: 'Not Started', sequence: 4 }
        ]
      }
    ],
    
    // SOC Audit Plan template
    'SOC': [
      {
        phase: 'Planning',
        order: 1,
        activities: [
          { activity: 'Define SOC report type (SOC 1, SOC 2, SOC 3)', status: 'Not Started', sequence: 0 },
          { activity: 'Determine scope of services and systems', status: 'Not Started', sequence: 1 },
          { activity: 'Select applicable trust services criteria', status: 'Not Started', sequence: 2 },
          { activity: 'Define assessment period', status: 'Not Started', sequence: 3 },
          { activity: 'Schedule kickoff meeting with key stakeholders', status: 'Not Started', sequence: 4 }
        ]
      },
      {
        phase: 'System Description',
        order: 2,
        activities: [
          { activity: 'Document system components and boundaries', status: 'Not Started', sequence: 0 },
          { activity: 'Prepare system description', status: 'Not Started', sequence: 1 },
          { activity: 'Document service commitments and requirements', status: 'Not Started', sequence: 2 },
          { activity: 'Map system components to applicable criteria', status: 'Not Started', sequence: 3 },
          { activity: 'Review system description with management', status: 'Not Started', sequence: 4 }
        ]
      },
      {
        phase: 'Risk Assessment',
        order: 3,
        activities: [
          { activity: 'Identify risks to achievement of service commitments', status: 'Not Started', sequence: 0 },
          { activity: 'Evaluate risk severity and likelihood', status: 'Not Started', sequence: 1 },
          { activity: 'Identify complementary user entity controls', status: 'Not Started', sequence: 2 },
          { activity: 'Document complementary subservice organization controls', status: 'Not Started', sequence: 3 }
        ]
      },
      {
        phase: 'Control Mapping',
        order: 4,
        activities: [
          { activity: 'Identify controls addressing relevant criteria', status: 'Not Started', sequence: 0 },
          { activity: 'Document control design', status: 'Not Started', sequence: 1 },
          { activity: 'Map controls to risks', status: 'Not Started', sequence: 2 },
          { activity: 'Prepare control matrices', status: 'Not Started', sequence: 3 },
          { activity: 'Validate control matrices with control owners', status: 'Not Started', sequence: 4 }
        ]
      },
      {
        phase: 'Testing',
        order: 5,
        activities: [
          { activity: 'Develop test plans and sampling methodology', status: 'Not Started', sequence: 0 },
          { activity: 'Test design effectiveness of controls', status: 'Not Started', sequence: 1 },
          { activity: 'Test operating effectiveness of controls', status: 'Not Started', sequence: 2 },
          { activity: 'Document test results and deviations', status: 'Not Started', sequence: 3 },
          { activity: 'Evaluate impact of control deviations', status: 'Not Started', sequence: 4 }
        ]
      },
      {
        phase: 'Reporting',
        order: 6,
        activities: [
          { activity: 'Draft management assertion', status: 'Not Started', sequence: 0 },
          { activity: 'Prepare SOC report', status: 'Not Started', sequence: 1 },
          { activity: 'Develop remediation plans for exceptions', status: 'Not Started', sequence: 2 },
          { activity: 'Present findings to management', status: 'Not Started', sequence: 3 },
          { activity: 'Finalize and issue SOC report', status: 'Not Started', sequence: 4 }
        ]
      }
    ],
    
    // Custom template (basic structure)
    'Custom': [
      {
        phase: 'Planning',
        order: 1,
        activities: [
          { activity: 'Define audit scope and objectives', status: 'Not Started', sequence: 0 },
          { activity: 'Schedule kickoff meeting', status: 'Not Started', sequence: 1 }
        ]
      },
      {
        phase: 'Fieldwork',
        order: 2,
        activities: [
          { activity: 'Gather and analyze information', status: 'Not Started', sequence: 0 },
          { activity: 'Perform audit procedures', status: 'Not Started', sequence: 1 }
        ]
      },
      {
        phase: 'Reporting',
        order: 3,
        activities: [
          { activity: 'Document findings and recommendations', status: 'Not Started', sequence: 0 },
          { activity: 'Prepare final report', status: 'Not Started', sequence: 1 }
        ]
      }
    ]
  };

  // Fetch project details and planning documents
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch project details
        const projectData = await auditService.fetchProjectsById(id);
        setProject(projectData);
        
        // Fetch all planning documents for this project
        const documentTypesResponse = await auditService.fetchAuditPlanDocuments(id);
        
        // Process each document type response
        const documentsList = documentTypesResponse.map(docTypeData => {
          const docType = docTypeData.DOCUMENT_TYPE;
          
          // Find the corresponding document type info
          const docTypeInfo = documentTypes.find(type => type.id === docType);
          
          // Get items for this document type
          const items = docTypeData.items || [];
          
          // Calculate progress statistics
          const totalActivities = items.length;
          const completedActivities = items.filter(item => item.STATUS === 'Completed').length;
          const inProgressActivities = items.filter(item => item.STATUS === 'In Progress').length;
          const notStartedActivities = items.filter(item => item.STATUS === 'Not Started' || !item.STATUS).length;
          
          // Calculate progress percentage
          const progressPercent = totalActivities > 0 
            ? Math.round((completedActivities / totalActivities) * 100) 
            : 0;
          
          return {
            type: docType,
            name: docType === 'Custom' && docTypeData.CUSTOM_NAME 
              ? docTypeData.CUSTOM_NAME
              : docTypeInfo 
                ? docTypeInfo.name 
                : docType,
            icon: docTypeInfo ? docTypeInfo.icon : <Icons.Assignment />,
            color: docTypeInfo ? docTypeInfo.color : '#2563eb',
            description: docTypeInfo ? docTypeInfo.description : 'Planning document',
            count: totalActivities,
            items: items,
            totalActivities,
            completedActivities,
            inProgressActivities,
            notStartedActivities,
            progressPercent,
            customName: docTypeData.CUSTOM_NAME
          };
        });
        
        setPlanDocuments(documentsList);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching planning documents:', error);
        enqueueSnackbar('Failed to load planning documents', { variant: 'error' });
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);

  // Handle creating a new planning document
  const handleCreateDocument = async () => {
    if (!newDocumentType) {
      enqueueSnackbar('Please select a document type', { variant: 'warning' });
      return;
    }
    
    // For custom document, require a name
    if (newDocumentType === 'Custom' && !customDocumentName.trim()) {
      enqueueSnackbar('Please enter a name for your custom document', { variant: 'warning' });
      return;
    }
    
    try {
      setLoading(true);
      
      // Get the template for the selected document type
      const template = templateLibrary[newDocumentType] || templateLibrary['General'];
      const totalActivities = template.reduce((count, phase) => count + phase.activities.length, 0);
      
      // Show progress message
      enqueueSnackbar(`Creating planning document with ${totalActivities} activities...`, { 
        variant: 'info',
        autoHideDuration: 3000
      });
      
      // Check if this document type already exists
      const existingDoc = planDocuments.find(doc => doc.type === newDocumentType);
      if (existingDoc) {
        setLoading(false);
        enqueueSnackbar(`A ${newDocumentType} planning document already exists. Please use a different type or delete the existing one first.`, { 
          variant: 'warning' 
        });
        return;
      }
      
      // Create all activities based on the template
      for (const phase of template) {
        for (const activityItem of phase.activities) {
          // Create a new activity with only the necessary properties
          const planItem = {
            AUDIT_ID: id,
            COMPANY_ID: project?.COMPANY_ID?.id,
            DOCUMENT_TYPE: newDocumentType,
            PHASE: phase.phase,
            ACTIVITY: activityItem.activity,
            STATUS: activityItem.status || 'Not Started',
            SEQUENCE: activityItem.sequence || 0
          };
          
          // If custom, add the custom name
          if (newDocumentType === 'Custom') {
            planItem.CUSTOM_NAME = customDocumentName;
          }
          
          // Create the document entry
          await auditService.createAuditPlan(planItem);
        }
      }
      
      // Reset form
      setNewDocumentType('');
      setCustomDocumentName('');
      setShowTemplatePreview(false);
      setCreateDialogOpen(false);
      
      // Success message
      enqueueSnackbar(`Planning document created successfully with ${totalActivities} activities`, { 
        variant: 'success' 
      });
      
      // Navigate to the planning details with the selected document type
      navigate(`/audit/projects/${id}/planning/${newDocumentType}`);
      
    } catch (error) {
      console.error('Error creating planning document:', error);
      enqueueSnackbar('Failed to create planning document', { variant: 'error' });
      setLoading(false);
    }
  };

  // Handle confirmation of document deletion
  const confirmDeleteDocument = (docType) => {
    setDocumentToDelete(docType);
    setDeleteDialogOpen(true);
  };
  
  // Handle document deletion
  const handleDeleteDocument = async () => {
    if (!documentToDelete) return;
    
    try {
      setLoading(true);
      
      // Get all activities for this document type only
      const itemsToDelete = planDocuments
        .find(doc => doc.type === documentToDelete)?.items || [];
      
      // Delete all items for this specific document type
      for (const item of itemsToDelete) {
        await auditService.deleteAuditPlan(item.id);
      }
      
      // Update local state by removing only the deleted document type
      setPlanDocuments(prevDocs => prevDocs.filter(doc => doc.type !== documentToDelete));
      
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
      setLoading(false);
      
      enqueueSnackbar(`${documentTypes.find(t => t.id === documentToDelete)?.name || documentToDelete} planning document deleted successfully`, { variant: 'success' });
    } catch (error) {
      console.error('Error deleting planning document:', error);
      enqueueSnackbar('Failed to delete planning document', { variant: 'error' });
      setLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  // Navigate to the detailed planning page for a specific document type
  const handleOpenDocument = (docType) => {
    navigate(`/audit/projects/${id}/planning/${docType}`);
  };

  // Handle template preview toggle
  const toggleTemplatePreview = () => {
    setShowTemplatePreview(!showTemplatePreview);
  };

  // Reset create dialog state
  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setNewDocumentType('');
    setCustomDocumentName('');
    setShowTemplatePreview(false);
  };

  const mainContent = (
    <ResponsiveContainer>
      <mui.Box sx={{ padding: { xs: '16px', sm: '24px' }, maxWidth: '1600px', margin: '0 auto' }}>
        {/* Breadcrumbs */}
        <mui.Breadcrumbs 
          aria-label="breadcrumb"
          sx={{
            '& .MuiLink-root': {
              display: 'flex',
              alignItems: 'center',
              color: 'text.secondary',
              fontSize: '0.875rem',
              transition: 'color 0.2s ease',
              '&:hover': {
                color: 'primary.main'
              }
            }
          }}
        >
          <mui.Link underline="hover" color="inherit" href="/dashboard">
            <Icons.Home sx={{ mr: 0.5, fontSize: 16 }} />
            Home
          </mui.Link>
          <mui.Link underline="hover" color="inherit" href="/Audit/Projects">
            Audit Projects
          </mui.Link>
          <mui.Link underline="hover" color="inherit" href={`/audit/projects/${id}`}>
            Project Settings
          </mui.Link>
          <mui.Typography color="text.primary">Planning Documents</mui.Typography>
        </mui.Breadcrumbs>

        {/* Header */}
        <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 4 }}>
          <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Icons.FolderOpen sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
            <mui.Typography variant="h4" component="h1" fontWeight="600">
              Planning Documents
            </mui.Typography>
          </mui.Box>
          <mui.Button
            variant="contained"
            color="primary"
            startIcon={<Icons.Add />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{ borderRadius: '8px' }}
          >
            Create New Plan
          </mui.Button>
        </mui.Box>

        {/* Project Info Card */}
        {project && (
          <mui.Paper sx={{ 
            p: 3, 
            mb: 4, 
            borderRadius: 2,
            backgroundColor: '#f0f9ff',
            border: '1px solid',
            borderColor: 'rgba(148, 163, 184, 0.1)'
          }}>
            <mui.Grid container spacing={2}>
              <mui.Grid item xs={12} md={6}>
                <mui.Typography variant="h6" fontWeight="600" mb={1}>
                  {project.AUDIT_NAME}
                </mui.Typography>
                <mui.Typography variant="body2" color="text.secondary" paragraph>
                  {project.AUDIT_DESCRIPTION}
                </mui.Typography>
                <mui.Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <mui.Chip
                    size="small"
                    label={project.AUDIT_TYPE || 'N/A'}
                    sx={{ 
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      color: 'primary.main',
                      fontWeight: 500,
                      mr: 1
                    }}
                  />
                  <mui.Chip
                    size="small"
                    label={project.STATUS || 'N/A'}
                    sx={{ 
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      color: 'primary.main',
                      fontWeight: 500
                    }}
                  />
                </mui.Box>
              </mui.Grid>
              <mui.Grid item xs={12} md={6}>
                <mui.Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, mt: { xs: 2, md: 0 } }}>
                  <mui.Box>
                    <mui.Typography variant="subtitle2" color="text.secondary">
                      End Date
                    </mui.Typography>
                    <mui.Typography variant="body1" fontWeight="500">
                      {project.PERIOD_END_DATE ? new Date(project.PERIOD_END_DATE).toLocaleDateString(undefined, {
                        year: 'numeric', month: 'short', day: 'numeric'
                      }) : 'Not set'}
                    </mui.Typography>
                    
                    <mui.Typography variant="subtitle2" color="text.secondary" mt={2}>
                      Project Owner
                    </mui.Typography>
                    <mui.Typography variant="body1" fontWeight="500">
                      {project.CREATED_BY || 'Not assigned'}
                    </mui.Typography>
                  </mui.Box>
                </mui.Box>
              </mui.Grid>
            </mui.Grid>
          </mui.Paper>
        )}

        {/* Loading State */}
        {loading ? (
          <mui.Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <mui.CircularProgress />
          </mui.Box>
        ) : (
          <mui.Box>
            {/* Planning Documents Grid */}
            {planDocuments.length === 0 ? (
              <mui.Paper 
                sx={{ 
                  p: 4, 
                  textAlign: 'center', 
                  borderRadius: 2,
                  border: '1px dashed',
                  borderColor: 'rgba(148, 163, 184, 0.2)',
                  bgcolor: 'background.paper',
                  minHeight: '300px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Icons.FolderOpen sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <mui.Typography variant="h6" color="text.secondary" gutterBottom>
                  No Planning Documents Yet
                </mui.Typography>
                <mui.Typography variant="body2" color="text.secondary" paragraph>
                  Create a planning document to organize your audit activities.
                </mui.Typography>
                <mui.Button
                  variant="contained"
                  color="primary"
                  startIcon={<Icons.Add />}
                  onClick={() => setCreateDialogOpen(true)}
                  sx={{ mt: 2 }}
                >
                  Create New Plan
                </mui.Button>
              </mui.Paper>
            ) : (
              <mui.Grid container spacing={3}>
                {planDocuments.map((doc) => (
                  <mui.Grid item xs={12} md={6} lg={4} key={doc.type}>
                    <mui.Paper
                      sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        height: '100%',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': {
                          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                          transform: 'translateY(-4px)'
                        }
                      }}
                    >
                      <mui.Box
                        sx={{
                          height: 8,
                          bgcolor: doc.color,
                        }}
                      />
                      <mui.Box sx={{ p: 3 }}>
                        <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <mui.Avatar
                            sx={{
                              bgcolor: `${doc.color}15`,
                              color: doc.color,
                              width: 48,
                              height: 48
                            }}
                          >
                            {React.cloneElement(doc.icon, { fontSize: 'medium' })}
                          </mui.Avatar>
                          <mui.Box sx={{ ml: 2 }}>
                            <mui.Typography variant="h6" fontWeight="600">
                              {doc.name}
                            </mui.Typography>
                            <mui.Typography variant="body2" color="text.secondary">
                              {doc.count} activities
                            </mui.Typography>
                          </mui.Box>
                        </mui.Box>
                        
                        <mui.Typography variant="body2" color="text.secondary" paragraph>
                          {doc.description}
                        </mui.Typography>
                        
                        <mui.Box sx={{ mt: 3, mb: 2 }}>
                          <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <mui.Typography variant="body2" color="text.secondary">
                              Progress
                            </mui.Typography>
                            <mui.Typography variant="body2" fontWeight="600">
                              {doc.progressPercent}%
                            </mui.Typography>
                          </mui.Box>
                          <mui.LinearProgress
                            variant="determinate"
                            value={doc.progressPercent}
                            sx={{
                              height: 8,
                              borderRadius: 1,
                              bgcolor: 'rgba(0,0,0,0.05)',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: doc.color
                              }
                            }}
                          />
                        </mui.Box>
                        
                        <mui.Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
                          <mui.Chip
                            size="small"
                            icon={<Icons.CheckCircleOutline fontSize="small" />}
                            label={`${doc.completedActivities} Completed`}
                            sx={{
                              bgcolor: 'rgba(34, 197, 94, 0.1)',
                              color: 'rgb(34, 197, 94)',
                              '& .MuiChip-icon': {
                                color: 'rgb(34, 197, 94)'
                              }
                            }}
                          />
                          <mui.Chip
                            size="small"
                            icon={<Icons.Pending fontSize="small" />}
                            label={`${doc.notStartedActivities} Not Started`}
                            sx={{
                              bgcolor: 'rgba(148, 163, 184, 0.1)',
                              color: 'rgb(71, 85, 105)',
                              '& .MuiChip-icon': {
                                color: 'rgb(71, 85, 105)'
                              }
                            }}
                          />
                        </mui.Box>
                        
                        <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, pt: 3, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                          <mui.Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<Icons.Delete />}
                            onClick={() => confirmDeleteDocument(doc.type)}
                            sx={{ borderRadius: '8px' }}
                          >
                            Delete
                          </mui.Button>
                          <mui.Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => handleOpenDocument(doc.type)}
                            endIcon={<Icons.ArrowForward />}
                            sx={{ borderRadius: '8px' }}
                          >
                            Open Plan
                          </mui.Button>
                        </mui.Box>
                      </mui.Box>
                    </mui.Paper>
                  </mui.Grid>
                ))}
              </mui.Grid>
            )}
          </mui.Box>
        )}
      </mui.Box>

      {/* Create Document Dialog */}
      <mui.Dialog
        open={createDialogOpen}
        onClose={handleCloseCreateDialog}
        maxWidth="md"
        fullWidth
      >
        <mui.DialogTitle sx={{ fontWeight: 600 }}>
          Create New Planning Document
        </mui.DialogTitle>
        <mui.DialogContent>
          <mui.Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 1 }}>
            Select the type of planning document you want to create for your audit project.
          </mui.Typography>
          
          <mui.FormControl component="fieldset" fullWidth>
            <mui.RadioGroup
              value={newDocumentType}
              onChange={(e) => setNewDocumentType(e.target.value)}
            >
              <mui.Grid container spacing={2} sx={{ mt: 1 }}>
                {documentTypes.map((type) => (
                  <mui.Grid item xs={12} key={type.id}>
                    <mui.Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderColor: newDocumentType === type.id ? type.color : 'divider',
                        borderWidth: newDocumentType === type.id ? 2 : 1,
                        bgcolor: newDocumentType === type.id ? `${type.color}10` : 'transparent',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: type.color,
                          bgcolor: `${type.color}05`
                        }
                      }}
                    >
                      <mui.FormControlLabel
                        value={type.id}
                        control={<mui.Radio sx={{ color: type.color, '&.Mui-checked': { color: type.color } }} />}
                        label={
                          <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <mui.Avatar
                              sx={{
                                bgcolor: `${type.color}15`,
                                color: type.color,
                                width: 32,
                                height: 32,
                                mr: 1
                              }}
                            >
                              {React.cloneElement(type.icon, { fontSize: 'small' })}
                            </mui.Avatar>
                            <mui.Typography variant="subtitle1" fontWeight={500}>
                              {type.name}
                            </mui.Typography>
                          </mui.Box>
                        }
                        sx={{ width: '100%', ml: 0, mr: 0 }}
                      />
                      <mui.Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
                        {type.description}
                      </mui.Typography>
                    </mui.Paper>
                  </mui.Grid>
                ))}
              </mui.Grid>
            </mui.RadioGroup>
          </mui.FormControl>
          
          {/* Custom name field shown only when Custom is selected */}
          {newDocumentType === 'Custom' && (
            <mui.TextField
              label="Custom Document Name"
              value={customDocumentName}
              onChange={(e) => setCustomDocumentName(e.target.value)}
              fullWidth
              required
              margin="normal"
              placeholder="Enter a name for your custom plan"
              helperText="Provide a descriptive name for your custom planning document"
            />
          )}
          
          {/* Template Preview Button - only show when a document type is selected */}
          {newDocumentType && (
            <mui.Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <mui.Button
                variant="outlined"
                color="primary"
                onClick={toggleTemplatePreview}
                startIcon={showTemplatePreview ? <Icons.VisibilityOff /> : <Icons.Visibility />}
                size="small"
              >
                {showTemplatePreview ? 'Hide Template Preview' : 'Show Template Preview'}
              </mui.Button>
            </mui.Box>
          )}
          
          {/* Template Preview Section */}
          {showTemplatePreview && newDocumentType && (
            <mui.Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
              <mui.Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Template Preview: {documentTypes.find(t => t.id === newDocumentType)?.name}
              </mui.Typography>
              <mui.Typography variant="body2" color="text.secondary" gutterBottom>
                Your plan will be pre-populated with the following phases and activities:
              </mui.Typography>
              
              <mui.Box sx={{ mt: 2, maxHeight: '300px', overflow: 'auto' }}>
                {templateLibrary[newDocumentType]?.map((phase, phaseIndex) => (
                  <mui.Box key={`preview-${phaseIndex}`} sx={{ mb: 2 }}>
                    <mui.Typography 
                      variant="subtitle2" 
                      sx={{ 
                        fontWeight: 600, 
                        color: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        '&::before': {
                          content: '""',
                          display: 'inline-block',
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          mr: 1
                        }
                      }}
                    >
                      {phase.phase}
                    </mui.Typography>
                    
                    <mui.List dense sx={{ pl: 3 }}>
                      {phase.activities.map((activity, activityIndex) => (
                        <mui.ListItem key={`activity-${phaseIndex}-${activityIndex}`} sx={{ py: 0.5 }}>
                          <mui.ListItemIcon sx={{ minWidth: '24px' }}>
                            <Icons.Circle sx={{ fontSize: '8px', color: 'text.secondary' }} />
                          </mui.ListItemIcon>
                          <mui.ListItemText 
                            primary={activity.activity} 
                            primaryTypographyProps={{ 
                              variant: 'body2', 
                              sx: { color: 'text.primary' } 
                            }}
                          />
                        </mui.ListItem>
                      ))}
                    </mui.List>
                  </mui.Box>
                ))}
              </mui.Box>
              
              <mui.Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                <Icons.Info sx={{ fontSize: 'small', color: 'text.secondary', mr: 1 }} />
                <mui.Typography variant="caption" color="text.secondary">
                  Total: {templateLibrary[newDocumentType]?.reduce((count, phase) => count + phase.activities.length, 0)} activities across {templateLibrary[newDocumentType]?.length} phases
                </mui.Typography>
              </mui.Box>
            </mui.Box>
          )}
        </mui.DialogContent>
        <mui.DialogActions sx={{ px: 3, pb: 3 }}>
          <mui.Button
            onClick={handleCloseCreateDialog}
            variant="outlined"
            sx={{ borderRadius: '8px' }}
          >
            Cancel
          </mui.Button>
          <mui.Button
            onClick={handleCreateDocument}
            variant="contained"
            color="primary"
            sx={{ borderRadius: '8px' }}
            startIcon={<Icons.Add />}
            disabled={!newDocumentType || (newDocumentType === 'Custom' && !customDocumentName.trim())}
          >
            Create Document
          </mui.Button>
        </mui.DialogActions>
      </mui.Dialog>

      {/* Delete Confirmation Dialog */}
      <mui.Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <mui.DialogTitle sx={{ 
          fontWeight: 600,
          color: 'error.main',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Icons.Warning /> Delete Planning Document
        </mui.DialogTitle>
        <mui.DialogContent>
          <mui.Typography variant="body1" paragraph>
            Are you sure you want to delete this planning document? This action cannot be undone.
          </mui.Typography>
          {documentToDelete && (
            <mui.Paper sx={{ p: 2, bgcolor: 'error.lighter', color: 'error.dark', borderRadius: 1, mb: 2 }}>
              <mui.Typography variant="subtitle1" fontWeight="600">
                {documentTypes.find(t => t.id === documentToDelete)?.name || documentToDelete}
              </mui.Typography>
              <mui.Typography variant="body2" sx={{ mt: 1 }}>
                This will delete {planDocuments.find(doc => doc.type === documentToDelete)?.count || 0} activities
              </mui.Typography>
            </mui.Paper>
          )}
        </mui.DialogContent>
        <mui.DialogActions sx={{ px: 3, pb: 3 }}>
          <mui.Button 
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{ borderRadius: '8px' }}
          >
            Cancel
          </mui.Button>
          <mui.Button 
            onClick={handleDeleteDocument}
            variant="contained"
            color="error"
            sx={{ borderRadius: '8px' }}
            startIcon={<Icons.Delete />}
          >
            Delete Document
          </mui.Button>
        </mui.DialogActions>
      </mui.Dialog>
    </ResponsiveContainer>
  );

  return (
    <Suspense fallback={<mui.CircularProgress />}>
      <IASideBar mainContent={mainContent} />
    </Suspense>
  );
};

export default PlanningDocuments; 